import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
	AlertInput,
	IonModal,
	ModalController,
	Platform,
} from '@ionic/angular';

import {
	faDesktop,
	faDungeon,
	faHospital,
	faHospitalUser,
	faPrint,
	faRightFromBracket,
	faScrewdriverWrench,
	faUser,
} from '@fortawesome/free-solid-svg-icons';

import { datadogLogs, StatusType } from '@datadog/browser-logs';

import { environment } from 'src/environments/environment';
import buildNumber from 'src/environments/build-number.json';
import datadogConfig from 'src/environments/datadog.json';

import { AuthService } from 'src/app/services/auth.service';
import { StorageService } from 'src/app/services/storage.service';
import { PlatformDetectionService } from 'src/app/services/platform-detection.service';

import { BaseService } from './services/base.service';
import {
	IAuthorizedTenant,
	UserProfileModel,
} from './models/user-profile.model';
import { SelectItemModal } from './modals/select-item/select-item.modal';
import { PrinterModel } from './models/printer.model';
import { getTenantLabel } from './utils/tenant.util';
import { RestApiService } from './services';
import { ConfirmKioskModeModal } from './modals/confirm-kiosk-mode/confirm-kiosk-mode.modal';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent {
	@ViewChild(IonModal) modal: IonModal;

	isDebug: boolean;
	isMenuDisabled: boolean;

	get user() {
		return StorageService?.user;
	}

	get tenant() {
		return StorageService?.activeTenant;
	}

	get preferredTenant() {
		return StorageService?.preferredTenant;
	}

	get authorizedTenants() {
		return StorageService?.user?.authorizedTenants;
	}

	/**
	 * If the user has no last saved/used printer, update the current printer to
	 * the one saved in local storage. If there is printer stored in local
	 * storage and also no last saved/used printer, display "None" instead of a
	 * printer name.
	 */
	get printer() {
		return StorageService?.lastUsedCachedPrinter;
	}

	/**
	 * Reactive PWA mode detection for UI elements
	 * Falls back to Ionic's platform detection if reactive detection isn't available
	 */
	get isPWAMode() {
		return this.platformDetection.isPWAMode;
	}

	StorageService = StorageService;
	faHospital = faHospital;
	faHospitalUser = faHospitalUser;
	faPrint = faPrint;
	faRightFromBracket = faRightFromBracket;
	faUser = faUser;
	faScrewdriverWrench = faScrewdriverWrench;
	faDesktop = faDesktop;
	faDungeon = faDungeon;

	getTenantLabel = getTenantLabel;

	constructor(
		public platform: Platform,
		private authService: AuthService,
		public modalCtrl: ModalController,
		public base: BaseService,
		private storage: StorageService, // instantiate/inject
		public router: Router,
		public http: RestApiService,
		private translate: TranslateService,
		private platformDetection: PlatformDetectionService,
	) {
		this.isDebug = !environment.production;

		this.initializeApp();

		this.translate.addLangs([
			'af',
			'am',
			'ar',
			'az',
			'bn',
			'bs',
			'bg',
			'ca',
			'zh',
			'hr',
			'cs',
			'da',
			'nl',
			'en',
			'et',
			'fi',
			'fr',
			'ka',
			'de',
			'el',
			'gu',
			'ht',
			'ha',
			'he',
			'hi',
			'hu',
			'is',
			'id',
			'ga',
			'it',
			'ja',
			'kn',
			'kk',
			'ko',
			'lv',
			'lt',
			'mk',
			'ms',
			'ml',
			'mt',
			'mr',
			'mn',
			'no',
			'fa',
			'ps',
			'pl',
			'pt',
			'pa',
			'ro',
			'ru',
			'sr',
			'si',
			'sk',
			'sl',
			'so',
			'es',
			'sw',
			'sv',
			'tl',
			'ta',
			'te',
			'th',
			'tr',
			'uk',
			'ur',
			'uz',
			'vi',
		]);
		this.translate.setDefaultLang('en');
		this.translate.use('en');
	}

	async initializeApp() {
		console.log('Initializing app');
		this.platform.ready().then(async () => {
			StorageService.isWeb =
				!this.platform.is('ios') && !this.platform.is('android');

			// store app configs
			StorageService.appConfigs =
				await this.authService.getAppConfigWithFallback();

			this.initializeDatadog();
			this.initializeDevTools();

			// if the route is "print-preview", then we don't want to load the configs
			if (this.router.url == '/print-preview') {
				return;
			}

			// We only want to send the user to the sign in page if they are on
			// a mobile device using the PWA. This was sending the developers
			// to the sign in page anytime they made a change to the frontend.
			// We check for the "clerk_token", and that the user is on mobile
			// and PWA.
			if (
				!this.router.url.includes('clerk_token') &&
				this.platform.is('mobile') &&
				this.platform.is('pwa')
			) {
				console.log('redirecting to sign in');
				window.location.href = environment.settings.clerkSignInURL;
				return;
			}

			// If already logged in, set necessary data (set user profile, set tenant, set configs)
			// This only really applies to dev builds if you are navigating within the app
			if (StorageService.authToken && !StorageService.user.isKioskSystemUser) {
				try {
					await this.base.authService.setUserProfile(); // retrieve user profile and store it
					await this.checkAuthorizedTenants(StorageService.user);
					await StorageService.setTenant();
					this.authService.initializeLicenses();
				} catch (e) {
					/*
						There's a chance that the user's authKey expired. The user should be logged out
						by the token interceptor, so we don't need to do anything here.
					*/
					console.error(e);
				}
			}
		});
	}

	initializeDatadog() {
		if (datadogConfig.enabled) {
			datadogLogs.init({
				clientToken: datadogConfig.clientToken,
				forwardErrorsToLogs: true,
				sessionSampleRate: 100,
				service: datadogConfig.service,
			});

			datadogLogs.logger.setLevel(datadogConfig.minimumLogLevel as StatusType);

			datadogLogs.setGlobalContext({
				env: environment.type,
				isProduction: environment.production,
				site: datadogConfig.site,
				version: buildNumber.GIT_SHA.substring(0, 7),
				platforms: this.platform.platforms(),
				baseUrl: environment.baseUrl,
			});

			datadogLogs.logger.debug('client initialized');
		}
	}

	/** Internal developer tools */
	initializeDevTools() {
		// simulate a scan
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		(window as any).scan = (value) => {
			setTimeout(() => {
				[...value].forEach((key) =>
					document.dispatchEvent(new KeyboardEvent('keydown', { key })),
				);
			}, 1500);
		};

		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		(window as any).scanmb = (value) => {
			setTimeout(() => {
				[...`M|${value}|B`].forEach((key) =>
					document.dispatchEvent(new KeyboardEvent('keydown', { key })),
				);
			}, 1500);
		};

		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		(window as any).scanmbp = (value) => {
			setTimeout(() => {
				[...`M|${value}|P`].forEach((key) =>
					document.dispatchEvent(new KeyboardEvent('keydown', { key })),
				);
			}, 1500);
		};
	}

	logout() {
		this.base.logout();
	}

	/**
	 * Show select printer modal when the user clicks on the current printer
	 * item in the menu.
	 */
	async handleSelectPrinterPressed() {
		const modal = await this.base.modalCtrl.create({
			component: SelectItemModal,
			componentProps: {
				title: 'Select Printer',
				items: StorageService.printers,
				searchProperties: {
					title: 'name',
					subtitle: '',
					endText: '',
				},
			},
			cssClass: 'fullscreen-modal',
		});

		modal.onDidDismiss().then((data) => {
			const selectedPrinter: PrinterModel = data?.data;
			if (selectedPrinter) {
				this.base.authService.updateUserProfile({
					currentPrinterId: selectedPrinter?.id,
					preferredTenantId: this.preferredTenant?.tenantId
				});
				StorageService.lastUsedCachedPrinter = selectedPrinter;

				console.log(
					`PrintLabelComponent.handleSelectPrinterPress onDidDismiss: ${JSON.stringify(
						selectedPrinter,
						null,
						2,
					)}`,
				);
			} else {
				console.log(
					'PrintLabelComponent.handleSelectPrinterPress onDidDismiss: no data passed',
				);
			}
		});

		return await modal.present();
	}

	getPreferredTenantLabel(
		preferredTenant: IAuthorizedTenant,
		authorizedTenants: IAuthorizedTenant[],
	) {
		return preferredTenant
			? getTenantLabel(preferredTenant, authorizedTenants)
			: 'None';
	}

	/**
	 * Show an alert where the user can select a tenant from a radio list of
	 * authorized tenants. The selected tenant is then set as the default tenant.
	 */
	async selectTenant() {
		const options: AlertInput[] = [];

		for (const [i, tenant] of this.authorizedTenants.entries()) {
			const label = getTenantLabel(tenant, this.authorizedTenants);

			const option: AlertInput = {
				name: `radio${i + 1}`, // i starts at 0, and selected tenant is index radio1
				type: 'radio',
				label,
				value: `${JSON.stringify(tenant)}`,
				checked: tenant.tenantId === StorageService.activeTenant?.tenantId,
			};
			options.push(option);
		}

		const alert = await this.base.alerts.createAlert({
			header: 'Select Tenant',
			message: 'Select the tenant you would like to use',
			inputs: options,
			backdropDismiss: false,
			buttons: [
				{
					text: 'Select',
					handler: async (value: string) => {
						const tenant: IAuthorizedTenant = JSON.parse(value);
						StorageService.saveTenant(tenant);
						StorageService.configs = await this.authService.getTenantConfigs(
							tenant.tenantId,
						);
						// Clear the current printer selection when switching tenants
						// When selecting a new printer, it will pull the list of printers for the new tenant
						StorageService.lastUsedCachedPrinter = null;
					},
				},
			],
		});

		await alert.present();
	}

	/**
	 * Show an alert where the user can select a tenant from a radio list of
	 * authorized tenants. The selected tenant is then set as the default tenant.
	 */
	async selectPreferredTenant() {
		const options: AlertInput[] = [];
		for (const [i, tenant] of this.authorizedTenants.entries()) {
			const label = getTenantLabel(tenant, this.authorizedTenants);

			const option: AlertInput = {
				name: `radio${i + 1}`,
				type: 'radio',
				label,
				value: tenant.tenantId,
				checked: tenant.isPreferredTenant,
			};
			options.push(option);
		}

		const alert = await this.base.alerts.createAlert({
			header: 'Set your home unit',
			message: 'This setting will be remembered every time you login.',
			inputs: options,
			backdropDismiss: false,
			buttons: [
				{
					text: 'Select',
					handler: async (tenantId: IAuthorizedTenant['tenantId']) => {
						StorageService.user = await this.authService.updateUserProfile({
							currentPrinterId:  StorageService.lastUsedCachedPrinter?.id,
							preferredTenantId: tenantId,
						});

						StorageService.preferredTenant =
							StorageService.user.authorizedTenants.find(
								(x) => x.tenantId === StorageService.user.preferredTenantId,
							);
					},
				},
			],
		});

		await alert.present();
	}

	/**
	 * If a user has an invalid authKey
	 * 	  the user is redirected to the login page
	 * If the user does not have an authKey
	 * 	  take the user to /portal since that route will handle the redirect.
	 */
	goToAdminPortal() {
		const userProfile = StorageService.user;
		const authenticatedRoute = `${environment.portalUrl}/account/app-auth/${userProfile.authKey}`;
		const portalRoute = environment.portalUrl;
		const route = userProfile.authKey ? authenticatedRoute : portalRoute;
		window.open(route, '_self');
	}

	/**
	 * If the user has the kiosk permission
	 * 	  take the user to /kiosk-start
	 */
	async goToKioskMode() {
		const modal = await this.base.modalCtrl.create({
			component: SelectItemModal,
			componentProps: {
				title: 'Select Printer',
				items: StorageService.printers,
				searchProperties: {
					title: 'name',
					subtitle: '',
					endText: '',
				},
			},
			cssClass: 'fullscreen-modal',
		});

		modal.onDidDismiss().then(async (data) => {
			const selectedPrinter: PrinterModel = data?.data;
			if (selectedPrinter) {
				this.base.authService.updateUserProfile({
					currentPrinterId: selectedPrinter?.id,
					preferredTenantId: this.preferredTenant?.tenantId
				});

				StorageService.lastUsedCachedPrinter = selectedPrinter;

				const confirmKioskModeModal = await this.modalCtrl.create({
					component: ConfirmKioskModeModal,
				});

				await confirmKioskModeModal.present();
			}
		});

		return await modal.present();
	}

	/**
	 * If the user has no authorized tenants, display a message and cancel the login
	 *
	 * TODO: This is duplicated in {@link LoginPage} and {@link LoginCrosswalkPage}
	 */
	async checkAuthorizedTenants(user: UserProfileModel) {
		if (user.authorizedTenants && user.authorizedTenants.length === 0) {
			datadogLogs.logger.info(`user has no authorized tenants: ${user.email}`);
			await this.base.alerts.presentAlert({
				header: 'Login Failed',
				message: [
					'You do not have access to any units in MilkTracker. </br></br>',
					'Please verify with hospital IT support that you have been added to the appropriate access group and try log in again.',
				].join(''),
			});
			throw Error('User has no authorized tenants');
		}
	}
}
